import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const TargetMatrixSelect = () => {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadPeriods();
  }, []);

  const loadPeriods = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/matrix/select/periods');
      if (response.data.success) {
        setPeriods(response.data.data || []);
      }
    } catch (err) {
      console.error('Error loading periods:', err);
      setError('Erro ao carregar períodos disponíveis');
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodSelect = (period) => {
    navigate(`/matrix/${period}`);
  };

  const getPeriodIcon = (period) => {
    switch (period) {
      case 'daily':
        return 'fa-calendar-day';
      case 'weekly':
        return 'fa-calendar-week';
      case 'monthly':
        return 'fa-calendar-alt';
      case 'quarterly':
        return 'fa-calendar';
      case 'annual':
        return 'fa-chart-line';
      default:
        return 'fa-calendar';
    }
  };

  const getPeriodSubtitle = (period) => {
    switch (period) {
      case 'daily':
        return 'Visualizar semana atual';
      case 'weekly':
        return 'Visualizar mês atual';
      case 'monthly':
        return 'Visualizar ano atual';
      case 'quarterly':
        return 'Visualizar trimestres';
      case 'annual':
        return 'Visualizar anos';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="goals-list-page">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="goals-list-page">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="goals-list-page">
      <div className="container-fluid py-4">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-11 col-xl-10">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="flex-grow-1">
                <h1 className="mb-1">
                  <i className="fas fa-table me-2"></i>Matriz de Metas
                </h1>
                <p className="mb-0">Selecione o período das metas para visualizar a matriz</p>
              </div>
              <a href="/goals" className="btn btn-light ms-3">
                <i className="fas fa-arrow-left me-2"></i>Voltar
              </a>
            </div>

            {/* Instruções */}
            <div className="card mb-4" style={{ background: '#f8f9fa', border: 'none' }}>
              <div className="card-body">
                <div className="d-flex align-items-start">
                  <div className="me-3">
                    <i className="fas fa-info-circle" style={{ fontSize: '1.5rem', color: '#c5a572' }}></i>
                  </div>
                  <div className="flex-grow-1">
                    <h6 className="mb-3">
                      <i className="fas fa-lightbulb me-2"></i>Como funciona a matriz?
                    </h6>
                    <ul className="mb-0">
                      <li><strong>Diário:</strong> Mostra uma semana (7 dias)</li>
                      <li><strong>Semanal:</strong> Mostra um mês (4-5 semanas)</li>
                      <li><strong>Mensal:</strong> Mostra um ano (12 meses)</li>
                      <li><strong>Trimestral:</strong> Mostra 4 trimestres</li>
                      <li><strong>Anual:</strong> Mostra 3 anos</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Cards de Período */}
            {periods.length > 0 ? (
              <div className="row g-4">
                {periods.map((period) => (
                  <div key={period.value} className="col-md-6 col-lg-4">
                    <div
                      className="card h-100"
                      role="button"
                      tabIndex={0}
                      onClick={() => handlePeriodSelect(period.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handlePeriodSelect(period.value);
                        }
                      }}
                      style={{
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        border: '2px solid #e9ecef'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(197, 165, 114, 0.2)';
                        e.currentTarget.style.borderColor = '#c5a572';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.borderColor = '#e9ecef';
                      }}
                    >
                      <div className="card-body text-center">
                        <div className="mb-3" style={{ fontSize: '3rem', color: '#c5a572' }}>
                          <i className={`fas ${getPeriodIcon(period.value)}`}></i>
                        </div>
                        <div className="h5 mb-2" style={{ fontWeight: 600, color: '#2c3e50' }}>
                          {period.label}
                        </div>
                        <div className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
                          {getPeriodSubtitle(period.value)}
                        </div>
                        <div className="mb-3">
                          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#c5a572' }}>
                            {period.goal_count}
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                            metas
                          </div>
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                          <i className="fas fa-hand-pointer me-1"></i>Clique para selecionar
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="alert alert-warning shadow-sm">
                <div className="d-flex align-items-center">
                  <i className="fas fa-exclamation-triangle me-3" style={{ fontSize: '2rem' }}></i>
                  <div>
                    <h5 className="alert-heading mb-2">Nenhuma meta encontrada</h5>
                    <p className="mb-0">Crie metas primeiro para visualizar a matriz.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TargetMatrixSelect;
